import React, { FC, useContext, useRef, useState, MouseEvent } from 'react'
import { BtnBack, Button, Fonts, ScrollPage, Item as ItemBase } from 'components/base-ui'
import MemberShipGonGangCard from 'components/membership/MemberShipGonGangCard'
import { AppContext } from 'contexts/AppProvider'
import useLanguage from 'hooks/useLanguage'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { ThemeColor } from 'types/color'
import { PaymentTypeEnum, type PaymentMethodQuery } from 'graphQL/dinein/generate/operations'
import PaymentMethodItem from 'components/dine-in/PaymentMethodItem'
import MenuItem from 'components/base-ui/MenuItem'
import { displayPrice2Digit } from 'helpers'
import { hasAdjustmentsExceedingCashVoucher } from 'helpers/cart'
import OrderAdjustment, { type AutoPromotion } from './OrderAdjustment'
import { DetailItem as Item, Adjustments, AdjustmentTotal } from './checkoutStyled'
import usePaymentMethod from 'hooks/buffet/usePaymentMethod'
import { CurrentOrder } from 'hooks/buffet/useBuffetCheckout'
import MembershipHatoHeart from 'components/MembershipHatoHeart'
import useModal from 'hooks/useModal'
import ModalAutoPromotion from 'components/modals/ModalAutoPromotion'
import ModalConfirmUseExceedVoucher from 'components/modals/ModalConfirmUseExceedVoucher'
import { THEMES_UI } from 'brands'
import { vendorSelector } from 'store/slices/vendorSlice'
import { useSelector } from 'react-redux'

export enum CheckoutMode {
  DineIn = 'DineIn',
  Buffet = 'Buffet',
}

export interface Vendor {
  id: number
  nameEn: string
  nameTh: string
  logoImage: { resizableURL: string; originalURL: string }
}

type Props = {
  handleBack: () => void
  onClickDiscount: () => void
  onClickPaymentItem: () => void
  onCheckout: () => void
  isShowMemberCard: boolean
  isCheckoutProcessing: boolean
  vendor: Vendor
  order: CurrentOrder
  buffetLineItemsTotal?: { packageTotal: number; overcharge: number }
  mode: CheckoutMode
  paymentMethods: PaymentTypeEnum[]
  refetchPayment: () => Promise<PaymentMethodQuery>
}

const CheckoutPresenter: FC<Props> = ({
  handleBack,
  onClickDiscount,
  onClickPaymentItem,
  onCheckout,
  isShowMemberCard,
  vendor,
  order,
  isCheckoutProcessing,
  buffetLineItemsTotal,
  mode,
  paymentMethods,
  refetchPayment,
}) => {
  const { t } = useTranslation()
  const { getPropertyByLang } = useLanguage()
  const { currentMembership, themeUI } = useContext(AppContext)
  const scrollPageRef = useRef<HTMLDivElement>(null)
  const [selectedAutoPromotion, setSelectedAutoPromotion] = useState({ name: '', description: '' })
  const modalAutoPromotion = useModal()
  const modalConfirmUseExceedVoucher = useModal()
  const isZeroCart = order.totalDueSatangs === 0

  const { paymentMethod } = usePaymentMethod({
    currentOrder: order!,
    paymentMethods: paymentMethods,
    refetchData: refetchPayment,
  })

  const handleClickAutoPromotion = (autoPromotion: AutoPromotion) => {
    setSelectedAutoPromotion(autoPromotion)
    modalAutoPromotion.setOpen(true)
  }

  const handleCloseModalAutoPromotion = () => {
    setSelectedAutoPromotion({ name: '', description: '' })
    modalAutoPromotion.setOpen(false)
  }

  function handleVoucherOnCheckout(e: MouseEvent<HTMLElement>) {
    e.preventDefault()
    const hasExceedingCashVoucher = hasAdjustmentsExceedingCashVoucher(order.adjustments)

    if (hasExceedingCashVoucher) {
      modalConfirmUseExceedVoucher.setOpen(true)
    } else {
      onCheckout()
    }
  }

  const {
    setting: { excludingVat },
  } = useSelector(vendorSelector)

  const disabledCheckout =
    (paymentMethod.type === PaymentTypeEnum.None && order.totalDueSatangs > 0) || isCheckoutProcessing

  return (
    <ScrollPage id="checkout-page" ref={scrollPageRef}>
      <Wrapper>
        <BtnBack
          data-testid="btn-back-checkout"
          enableFastClick={false}
          style={{ marginBottom: 32 }}
          bold
          color={ThemeColor.primary}
          onClick={handleBack}
          disabled={isCheckoutProcessing}
        />

        {currentMembership &&
          (isShowMemberCard ? (
            <MemberShipGonGangCard
              membership={currentMembership}
              brandLogo={vendor.logoImage?.resizableURL.replace('{width}x{height}', '200x200_fmt-png_fit-outside')}
              vendorName={getPropertyByLang({ en: vendor.nameTh, th: vendor.nameEn })}
            />
          ) : (
            <MembershipHatoHeart
              membership={currentMembership}
              brandLogo={vendor.logoImage?.resizableURL.replace('{width}x{height}', '200x200_fmt-png_fit-outside')}
              vendorName={getPropertyByLang({ en: vendor.nameTh, th: vendor.nameEn })}
            />
          ))}

        <Fonts.H2>{t('dineInPayment')}</Fonts.H2>
        <MenuWrapper>
          {paymentMethod.type === PaymentTypeEnum.None ? (
            isZeroCart ? (
              <ItemBase
                highlight
                mainIcon="coupon"
                iconSize="11px"
                rightIconSize="16px"
                rightIcon="arrow-right-bold"
                disabled={true}
                iconAlignTop
              >
                <Fonts.BaseTitleBold>Voucher</Fonts.BaseTitleBold>
                <Fonts.SmallTitleRegular>{t('checkout.form.exceededVoucher')}</Fonts.SmallTitleRegular>
              </ItemBase>
            ) : (
              <ItemBase
                testId="paymentTypeBtn"
                id="paymentMethodBtn"
                mainIcon="warning-bold"
                rightIcon="arrow-right-bold"
                rightIconSize="16px"
                highlight
                onClick={onClickPaymentItem}
              >
                <Fonts.BaseTitleBold>{t('checkout.form.selectPaymentmethod')}</Fonts.BaseTitleBold>
              </ItemBase>
            )
          ) : (
            <PaymentMethodItem paymentMethod={paymentMethod} isActive={true} onClick={onClickPaymentItem} />
          )}

          <MenuItem
            testId="promotionBtn"
            icon="percent"
            title={
              themeUI === THEMES_UI.GON ? t('btnCheckoutPromotionsAndVoucherPage') : t('btnCheckoutPromotionsPage')
            }
            iconAction="arrow-right-bold"
            isActive={true}
            onClick={onClickDiscount}
          />
        </MenuWrapper>
        <DetailsWrapper id="checkout-details">
          {mode === CheckoutMode.Buffet && buffetLineItemsTotal ? (
            <>
              <Item>
                <TitleRegular>{t('buffet.checkoutPage.totalBuffet')}</TitleRegular>
                <TitleRegular>{displayPrice2Digit(buffetLineItemsTotal.packageTotal / 100)}</TitleRegular>
              </Item>
              <Item>
                <TitleRegular>{t('buffet.checkoutPage.charge')}</TitleRegular>
                <TitleRegular>{displayPrice2Digit(buffetLineItemsTotal.overcharge / 100)}</TitleRegular>
              </Item>
              <OrderAdjustment adjustments={order.adjustments} handleClickAutoPromotion={handleClickAutoPromotion} />
              <Total>
                <Item>
                  <TitleRegular>{t('buffet.checkoutPage.totalExclude')}</TitleRegular>
                  <TitleRegular>{displayPrice2Digit(order.lineItemTotalSatangs / 100)}</TitleRegular>
                </Item>

                {order.adjustmentStaffCashTotalSatangs !== 0 && (
                  <Adjustments>
                    <TitleRegular>{t('useVoucherStaffPrivilege')}</TitleRegular>
                    <AdjustmentTotal>{displayPrice2Digit(order.adjustmentStaffCashTotalSatangs / 100)}</AdjustmentTotal>
                  </Adjustments>
                )}
                {!!excludingVat && (
                  <Item>
                    <TitleRegular>VAT 7%</TitleRegular>
                    <TitleRegular>{displayPrice2Digit(order.lineItemVatSatangs / 100)}</TitleRegular>
                  </Item>
                )}
              </Total>
              <Item>
                <Fonts.Medium>{t('dineInTotalPayment')}</Fonts.Medium>
                <Fonts.Medium>{displayPrice2Digit(order.totalDueSatangs / 100)}</Fonts.Medium>
              </Item>
            </>
          ) : (
            <>
              <Item>
                <TitleRegular>{t('dineInFoodPrice')}</TitleRegular>
                <TitleRegular>{displayPrice2Digit(order.lineItemTotalSatangs / 100)}</TitleRegular>
              </Item>
              <OrderAdjustment adjustments={order.adjustments} handleClickAutoPromotion={handleClickAutoPromotion} />
              <Total>
                <Item>
                  <TitleRegular>{t('dineInTotal')}</TitleRegular>
                  <TitleRegular>{displayPrice2Digit(order.totalSatangs / 100)}</TitleRegular>
                </Item>

                {order.adjustmentStaffCashTotalSatangs !== 0 && (
                  <Adjustments>
                    <TitleRegular>{t('useVoucherStaffPrivilege')}</TitleRegular>
                    <AdjustmentTotal>{displayPrice2Digit(order.adjustmentStaffCashTotalSatangs / 100)}</AdjustmentTotal>
                  </Adjustments>
                )}
                {!!excludingVat && (
                  <Item>
                    <TitleRegular>VAT 7%</TitleRegular>
                    <TitleRegular>{displayPrice2Digit(order.lineItemVatSatangs / 100)}</TitleRegular>
                  </Item>
                )}
              </Total>
              <Item>
                <Fonts.Medium>{t('dineInTotalPayment')}</Fonts.Medium>
                <Fonts.Medium>{displayPrice2Digit(order.totalDueSatangs / 100)}</Fonts.Medium>
              </Item>
            </>
          )}
        </DetailsWrapper>

        <PaymentButtonWrapper>
          <PaymentButton
            id="paymentMethodBtn"
            data-testid="paymentTypeBtn"
            onClick={handleVoucherOnCheckout}
            isProcessing={isCheckoutProcessing}
            disabled={disabledCheckout}
            enableFastClick={false}
          >
            {t('buffet.checkoutPage.checkout')}
          </PaymentButton>
        </PaymentButtonWrapper>

        <ModalAutoPromotion
          isOpen={modalAutoPromotion.open}
          adjustment={selectedAutoPromotion}
          onClose={handleCloseModalAutoPromotion}
        />

        {order && (
          <ModalConfirmUseExceedVoucher
            isOpen={modalConfirmUseExceedVoucher.open}
            adjustments={order.adjustments}
            onClickConfirm={() => {
              modalConfirmUseExceedVoucher.setOpen(false)
              onCheckout()
            }}
            onRequestClose={() => {
              modalConfirmUseExceedVoucher.setOpen(false)
            }}
            onClickGoBackToVoucherPage={() => {
              modalConfirmUseExceedVoucher.setOpen(false)
              onClickDiscount()
            }}
          />
        )}
      </Wrapper>
    </ScrollPage>
  )
}

export default CheckoutPresenter

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 15px;
  padding-bottom: 75px;
`
const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  padding-top: 14px;
  margin-top: 20px;
  border-top: 0.5px solid ${({ theme }) => theme.colors.baseGrey};
`

const MenuWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  padding-top: 14px;
`

const TitleRegular = styled(Fonts.BaseTitleRegular)`
  padding: 3px;
`

const Total = styled.div`
  padding-top: 14px;
  padding-bottom: 14px;
  border-top: 0.5px solid ${({ theme }) => theme.colors.baseGrey};
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.baseGrey};
`

const PaymentButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 20px;
`

const PaymentButton = styled(Button)`
  height: 50px;
  border-radius: 85px;
  :disabled {
    color: ${({ theme }) => theme.colors.white} !important;
  }
`
